import { useContext, type FC } from 'react';

import { assertNever } from 'app/utils/types';
import ErrorBoundary from 'app/common/ErrorBoundary';
import { isError } from 'app/utils/errors';
import { getVideogameFromComposedData } from 'app/utils/entities/data';
import { getBlocksToDisplay } from 'app/config/urlParams.config.helper';
import ThemeContext from 'app/common/ThemeContext';
import NameBlockGrad from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad';
import PlayersStatsBlockGrad from 'app/components/DesignVersion/Grad/Blocks/PlayersStatsBlockGrad';
import PlayersHeadToHeadBlockGrad from 'app/components/DesignVersion/Grad/Blocks/PlayersHeadToHeadBlockGrad';
import FormGuideBlockGrad from 'app/components/DesignVersion/Grad/Blocks/FormGuideBlockGrad';
import HeadToHeadBlockGrad from 'app/components/DesignVersion/Grad/Blocks/HeadToHeadBlockGrad';
import TeamsStatsBlockGrad from 'app/components/DesignVersion/Grad/Blocks/TeamsStatsBlockGrad';
import PoolBlockGrad from 'app/components/DesignVersion/Grad/Blocks/PoolBlockGrad';
import SeriesMatchesBlockGrad from 'app/components/DesignVersion/Grad/Blocks/SeriesMatchesBlockGrad';
import SeriesStatsBlockGrad from 'app/components/DesignVersion/Grad/Blocks/SeriesStatsBlockGrad';
import SeriesOverviewBlockGrad from 'app/components/DesignVersion/Grad/Blocks/SeriesOverviewBlockGrad';
import SeriesStandingsBlockGrad from 'app/components/DesignVersion/Grad/Blocks/SeriesStandingsBlockGrad';
import DisplayContext from 'app/common/DisplayContext';

import FormGuideBlock from './FormGuideBlock';
import HeadToHeadBlock from './HeadToHeadBlock';
import NameBlock from './NameBlock';
import PlayersStatsBlock from './PlayersStatsBlock';
import RostersBlock from './RostersBlock';
import TeamsStatsBlock from './TeamsStatsBlock';
import StreamBlock from './StreamBlock';
import BracketsBlock from './BracketsBlock';
import TournamentsBlock from './TournamentsBlock';
import { isValidBlockData } from './utils';

interface BlockProps {
  data: ComposedEntityData;
  block: Block;
}

// HANDLER: Block
const Block: FC<BlockProps> = ({ block, data }) => {
  const { designVersion } = useContext(ThemeContext);
  const blockData = data[block];

  if (isError(blockData)) {
    throw blockData;
  }

  if (blockData === null) {
    return null;
  }

  // Assert that data has valid block data
  if (!isValidBlockData(data, block as keyof BlockDataMap)) {
    return null; // This line should never be reached due to previous checks, but TypeScript needs it
  }

  switch (block) {
    case 'name': {
      if (designVersion === 'grad') {
        return <NameBlockGrad data={data.name} />;
      }
      return <NameBlock data={data.name} />;
    }
    case 'head-to-head': {
      if (designVersion === 'grad') {
        return <HeadToHeadBlockGrad data={data['head-to-head']} />;
      }
      return <HeadToHeadBlock data={data['head-to-head']} />;
    }
    case 'form-guide': {
      const { opponents } = data['form-guide'];
      if (designVersion === 'grad') {
        return <FormGuideBlockGrad opponents={opponents} />;
      }
      return <FormGuideBlock opponents={opponents} />;
    }
    case 'rosters': {
      const { opponents, videogame_slug } = data.rosters;
      return <RostersBlock opponents={opponents} videogame={videogame_slug} />;
    }
    case 'teams-stats': {
      if (designVersion === 'grad') {
        return <TeamsStatsBlockGrad data={data['teams-stats']} />;
      }
      return <TeamsStatsBlock data={data['teams-stats']} />;
    }
    case 'pool': {
      if (designVersion === 'grad') {
        return <PoolBlockGrad data={data.pool} />;
      }
      return null; // no player head to head block for default version
    }
    case 'players-stats': {
      if (designVersion === 'grad') {
        return <PlayersStatsBlockGrad data={data['players-stats']} />;
      }
      return <PlayersStatsBlock data={data['players-stats']} />;
    }
    case 'players-head-to-head': {
      if (designVersion === 'grad') {
        return <PlayersHeadToHeadBlockGrad data={data['players-head-to-head']} />;
      }
      return null; // no player head to head block for default version
    }
    case 'stream': {
      const { streams_list, videogame_slug } = data.stream;
      return <StreamBlock streams={streams_list} videogameSlug={videogame_slug} />;
    }
    case 'bracket': {
      return <BracketsBlock bracket={data.bracket} />;
    }
    case 'tournaments': {
      return <TournamentsBlock data={data.tournaments} />;
    }
    case 'series-tournaments': {
      if (designVersion === 'grad') {
        return <SeriesStandingsBlockGrad data={data['series-tournaments']} />;
      }
      return null; // no series standings block for default version
    }
    case 'series-matches': {
      if (designVersion === 'grad') {
        return <SeriesMatchesBlockGrad data={data['series-matches']} />;
      }
      return null; // no series standings block for default version
    }
    case 'series-stats': {
      if (designVersion === 'grad') {
        return <SeriesStatsBlockGrad data={data['series-stats']} />;
      }
      return null; // no series standings block for default version
    }
    case 'series-overview': {
      if (designVersion === 'grad') {
        return <SeriesOverviewBlockGrad data={data['series-overview']} />;
      }
      return null; // no series standings block for default version
    }

    default:
      throw assertNever(block);
  }
};

interface BlocksProps {
  data: ComposedEntityData;
  entityType: Entity;
  blocks: Block[];
}

const Blocks: FC<BlocksProps> = ({ blocks, data, entityType }) => {
  const { singleBlockDisplay } = useContext(DisplayContext);
  const videogame = getVideogameFromComposedData(data);
  const blocksToDisplay = getBlocksToDisplay(entityType, blocks, videogame);

  return (
    <>
      {blocksToDisplay.map((block, index) => (
        <ErrorBoundary
          key={`error_${block}_${index}`}
          skipRender={block === 'series-tournaments' && !singleBlockDisplay}
        >
          <Block block={block} data={data} />
        </ErrorBoundary>
      ))}
    </>
  );
};

Blocks.displayName = 'Blocks';

export default Blocks;
